import { LearnMoreLink } from '@auctisync/ui';
import { formatCents } from '@auctisync/shared';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { embedTitle } from '../lib/embedTitle';
import { SalesCompsModal } from './SalesCompsModal';

type CompResult = {
  match_count: number;
  sold_count: number;
  unsold_count: number;
  weighted_avg_cents: number | null;
  median_cents: number | null;
  min_cents: number | null;
  max_cents: number | null;
  last_sold_at: string | null;
  active_tier: number;
  tier_description: string;
  tier_1_count: number;
  tier_2_count: number;
  tier_3_count: number;
  tier_4_count: number;
  tier_5_count: number;
};

type Props = {
  tenantId: string | null | undefined;
  title: string | null | undefined;
  mpn?: string | null | undefined;
  categoryId?: string | null | undefined;
  categoryName?: string | null | undefined;
  condition?: string | null | undefined;
};

function TierCounts({ data }: { data: CompResult }) {
  const tiers: Array<{ n: number; label: string; count: number }> = [
    { n: 1, label: 'exact model', count: data.tier_1_count },
    { n: 2, label: 'same brand', count: data.tier_2_count },
    { n: 3, label: 'semantic match', count: data.tier_3_count },
    { n: 4, label: 'same category', count: data.tier_4_count },
    { n: 5, label: 'similar title', count: data.tier_5_count },
  ].filter((t) => t.count > 0);
  if (tiers.length <= 1) return null;
  return (
    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
      Other tiers available:{' '}
      {tiers
        .filter((t) => t.n !== data.active_tier)
        .map((t, i, arr) => (
          <span key={t.n}>
            {t.count} {t.label}
            {i < arr.length - 1 ? ' · ' : ''}
          </span>
        ))}
    </div>
  );
}

function Pill({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: '0.65rem',
          color: 'var(--text-muted)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          marginBottom: '0.15rem',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '0.95rem',
          fontWeight: 600,
          color: 'var(--text)',
          lineHeight: 1.1,
          wordBreak: 'break-word',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function daysAgo(iso: string): string {
  const days = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.round(days / 30)}mo ago`;
  return `${Math.round(days / 365)}y ago`;
}

/**
 * Compact card showing recency-weighted comp stats for a candidate lot.
 * Queries find_sales_comps with the item's title + optional category/condition
 * and debounces re-queries so rapid typing in the title field doesn't spam.
 */
export function SalesCompStrip({
  tenantId,
  title,
  mpn,
  categoryId,
  categoryName,
  condition,
}: Props) {
  const [data, setData] = useState<CompResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const trimmedTitle = (title ?? '').trim();

  useEffect(() => {
    if (!tenantId || trimmedTitle.length < 3) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      setLoading(true);
      setError(null);
      void (async () => {
        try {
          // Compute semantic embedding in parallel with the RPC prep. If the
          // Edge Function fails, we gracefully skip the semantic tier — the
          // RPC still returns brand/category/title matches.
          const embedding = await embedTitle(trimmedTitle);
          if (cancelled) return;
          const { data: rpcData, error: e } = await (
            supabase.rpc as unknown as (
              fn: string,
              args: Record<string, unknown>,
            ) => Promise<{ data: CompResult[] | CompResult | null; error: Error | null }>
          )('find_sales_comps', {
            p_tenant_id: tenantId,
            p_title: trimmedTitle,
            ...(mpn ? { p_mpn: mpn } : {}),
            ...(categoryId ? { p_category_id: categoryId } : {}),
            ...(categoryName ? { p_category_name: categoryName } : {}),
            ...(condition ? { p_condition: condition } : {}),
            ...(embedding ? { p_query_embedding: embedding } : {}),
          });
          if (cancelled) return;
          if (e) throw e;
          const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
          setData((row as CompResult) ?? null);
        } catch (err) {
          if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load comps');
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [tenantId, trimmedTitle, mpn, categoryId, categoryName, condition]);

  if (!tenantId || trimmedTitle.length < 3) return null;

  const hasSold = data && data.sold_count > 0;

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: '0.5rem',
        padding: '0.85rem 1rem',
        background: 'linear-gradient(90deg, rgba(30,39,97,0.04) 0%, rgba(30,39,97,0.00) 100%)',
        display: 'grid',
        gap: '0.5rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: '1rem',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <strong style={{ color: 'var(--text)', fontSize: '0.9rem' }}>Your comps</strong>
          <span
            style={{
              marginLeft: '0.6rem',
              color: 'var(--text-muted)',
              fontSize: '0.75rem',
              fontStyle: 'italic',
            }}
          >
            {loading
              ? 'searching…'
              : data && data.match_count > 0
                ? `matched on ${data.tier_description}`
                : ''}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'baseline' }}>
          {data && data.match_count > 0 && (
            <button
              type="button"
              onClick={() => setDetailOpen(true)}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                color: 'var(--accent, #1e2761)',
                fontSize: '0.75rem',
                fontWeight: 500,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              View details →
            </button>
          )}
          <LearnMoreLink helpSlug="inline-comps-on-lot-edit">How this works</LearnMoreLink>
        </div>
      </div>

      {error && <div style={{ fontSize: '0.85rem', color: 'var(--danger)' }}>{error}</div>}

      {!loading && data && data.match_count === 0 && (
        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          No comps yet for this title. As similar lots close, they&apos;ll show up here.
        </div>
      )}

      {!loading && data && data.match_count > 0 && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: '0.75rem',
            }}
          >
            <Pill label="Matched">
              {data.sold_count} sold
              {data.unsold_count > 0 ? ` / ${data.unsold_count} unsold` : ''}
            </Pill>
            {hasSold && (
              <>
                <Pill label="Weighted avg">
                  {data.weighted_avg_cents != null ? formatCents(data.weighted_avg_cents) : '—'}
                </Pill>
                <Pill label="Median">
                  {data.median_cents != null ? formatCents(data.median_cents) : '—'}
                </Pill>
                <Pill label="Range">
                  {data.min_cents != null && data.max_cents != null
                    ? `${formatCents(data.min_cents)} – ${formatCents(data.max_cents)}`
                    : '—'}
                </Pill>
                <Pill label="Last sold">
                  {data.last_sold_at ? daysAgo(data.last_sold_at) : '—'}
                </Pill>
              </>
            )}
          </div>
          {!hasSold && data.unsold_count > 0 && (
            <div style={{ fontSize: '0.8rem', color: 'var(--warning, #b45309)' }}>
              {data.unsold_count} similar lot{data.unsold_count === 1 ? '' : 's'} did not sell in
              this window. Consider a lower starting bid or no reserve.
            </div>
          )}
          <TierCounts data={data} />
        </>
      )}

      {detailOpen && tenantId && trimmedTitle.length > 0 && (
        <SalesCompsModal
          tenantId={tenantId}
          title={trimmedTitle}
          mpn={mpn}
          categoryId={categoryId}
          categoryName={categoryName}
          condition={condition}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </div>
  );
}
